import React from "react";

export interface ResizableTitleProps
    extends React.ThHTMLAttributes<HTMLTableCellElement> {
    width?: number;
    minWidth?: number;
    onResize?: (width: number) => void;
}

/**
 * Table header cell with a drag handle on its right edge. Plug into an
 * Ant Design Table via `components={{ header: { cell: ResizableTitle } }}`
 * and pass `width`/`onResize` from each column's `onHeaderCell`.
 */
const ResizableTitle: React.FC<ResizableTitleProps> = ({
    width,
    minWidth = 60,
    onResize,
    children,
    style,
    ...rest
}) => {
    if (!onResize || !width) {
        return (
            <th {...rest} style={style}>
                {children}
            </th>
        );
    }

    const handlePointerDown = (e: React.PointerEvent<HTMLSpanElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = width;

        const handleMove = (ev: PointerEvent) =>
            onResize(Math.max(minWidth, startWidth + ev.clientX - startX));
        const handleUp = () => {
            window.removeEventListener("pointermove", handleMove);
            window.removeEventListener("pointerup", handleUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("pointermove", handleMove);
        window.addEventListener("pointerup", handleUp);
    };

    return (
        <th {...rest} style={{ ...style, position: "relative" }}>
            {children}
            <span
                onPointerDown={handlePointerDown}
                // Keep the click from toggling the column sorter
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: "absolute",
                    top: 0,
                    right: -4,
                    bottom: 0,
                    width: 8,
                    cursor: "col-resize",
                    zIndex: 1,
                }}
            />
        </th>
    );
};

export default ResizableTitle;
